import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const FULL_DOCS_URL = 'https://api.deepnets.ai/docs';
const BASE_URL = 'https://api.deepnets.ai';
const TREASURY = 'Bn7sLNmcL9ru5kt9gNwVn8e8Sx912dBTxiUBNiwjbiY8';

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="bg-crypto-dark border border-gray-800 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-200">
    <code>{children}</code>
  </pre>
);

const ApiDocsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Helmet>
        <title>API Docs — Deepnets.ai</title>
        <meta
          name="description"
          content="Deepnets public API at api.deepnets.ai — authenticate with an API key, or pay per call in USDC via x402. Either works on the same base URL."
        />
      </Helmet>
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 w-full">
        <h1 className="text-3xl font-bold mb-3">API Documentation</h1>
        <p className="text-gray-400 mb-6">
          One base URL, two authentication methods. Use whichever fits — your choice is per-request, not account-wide.
        </p>

        {/* Base URL hero */}
        <div className="bg-crypto-gray/40 border border-crypto-accent/30 rounded-lg px-6 py-5 mb-8">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Base URL</div>
          <div className="font-mono text-xl md:text-2xl text-crypto-accent font-semibold break-all">
            {BASE_URL}
          </div>
        </div>

        {/* Two auth methods */}
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg p-6 flex flex-col">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-3">
              <span>🔑</span>
              <span>Option A — API key</span>
            </div>
            <div className="font-semibold text-white mb-2">Send <code className="font-mono text-crypto-accent">x-api-key</code></div>
            <p className="text-gray-400 text-sm mb-4 flex-1">
              Sign in, generate a key, include it on every request. Reads are included with your key; requests that trigger fresh research cost 1 credit.
            </p>
            <Link
              to="/api-keys"
              className="inline-block text-crypto-accent hover:text-white text-sm font-medium"
            >
              Manage API keys →
            </Link>
          </div>

          <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg p-6 flex flex-col">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-3">
              <span>💸</span>
              <span>Option B — Pay per call (x402)</span>
            </div>
            <div className="font-semibold text-white mb-2">Send <code className="font-mono text-crypto-accent">X-PAYMENT</code></div>
            <p className="text-gray-400 text-sm mb-4 flex-1">
              No account. Pay each call in USDC on Solana via the{' '}
              <a
                href="https://x402.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crypto-accent hover:text-white"
              >
                x402 protocol
              </a>
              . The Coinbase-hosted facilitator sponsors SOL gas — USDC is all you need.
            </p>
            <a
              href="https://x402.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-crypto-accent hover:text-white text-sm font-medium"
            >
              About x402 →
            </a>
          </div>
        </div>

        {/* How the server picks */}
        <h2 className="text-xl font-semibold mb-3">How the server decides</h2>
        <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg overflow-hidden mb-10">
          <table className="w-full text-sm">
            <thead className="bg-crypto-dark text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-5 py-3">Your request includes…</th>
                <th className="text-left px-5 py-3">Treated as</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              <tr>
                <td className="px-5 py-3 font-mono text-xs text-gray-300">x-api-key</td>
                <td className="px-5 py-3 text-gray-400">API-key flow (x402 is skipped)</td>
              </tr>
              <tr>
                <td className="px-5 py-3 font-mono text-xs text-gray-300">X-PAYMENT</td>
                <td className="px-5 py-3 text-gray-400">x402 flow — facilitator verifies + settles, handler runs</td>
              </tr>
              <tr>
                <td className="px-5 py-3 font-mono text-xs text-gray-300">Both</td>
                <td className="px-5 py-3 text-gray-400">API-key takes precedence; no payment is charged</td>
              </tr>
              <tr>
                <td className="px-5 py-3 text-gray-300">Neither, priced endpoint</td>
                <td className="px-5 py-3 text-gray-400"><code className="font-mono text-xs">402 Payment Required</code> with a <code className="font-mono text-xs">PAYMENT-REQUIRED</code> header</td>
              </tr>
              <tr>
                <td className="px-5 py-3 text-gray-300">Neither, unpriced endpoint</td>
                <td className="px-5 py-3 text-gray-400"><code className="font-mono text-xs">401</code> — API key required (x402 doesn't cover these)</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Full docs link */}
        <div className="bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg p-5 mb-10 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-white mb-1">Full endpoint reference</div>
            <p className="text-gray-400 text-sm">
              Schemas, parameters, response shapes, and every route — hosted on the API.
            </p>
          </div>
          <a
            href={FULL_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-5 py-2.5 rounded-lg font-medium whitespace-nowrap"
          >
            Open full docs →
          </a>
        </div>

        {/* x402 pricing */}
        <h2 className="text-xl font-semibold mb-3">x402 Pricing</h2>
        <p className="text-gray-400 text-sm mb-4">
          Per-call USDC pricing on Solana mainnet. Payments go to the deepnets treasury{' '}
          <span className="font-mono text-xs text-gray-300 break-all">{TREASURY}</span>.
        </p>
        <div className="bg-crypto-gray/40 border border-gray-800 rounded-lg overflow-hidden mb-10">
          <table className="w-full text-sm">
            <thead className="bg-crypto-dark text-gray-400 uppercase text-xs">
              <tr>
                <th className="text-left px-5 py-3">Tier</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Endpoints</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              <tr>
                <td className="px-5 py-3 font-medium text-white">Basic</td>
                <td className="px-5 py-3 font-mono text-gray-200">$0.01</td>
                <td className="px-5 py-3 text-gray-400">
                  token-safety, watchlist, trending, flagged-tokens, social-research, streamflow-locks, tibane-staking, token-safety-history, token stats + holder-graph, wallet-details, network-details
                </td>
              </tr>
              <tr>
                <td className="px-5 py-3 font-medium text-white">Holder analysis</td>
                <td className="px-5 py-3 font-mono text-gray-200">$0.15</td>
                <td className="px-5 py-3 text-gray-400">
                  /api/token-details?mint=…, /api/holder-analysis?mint=…
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-gray-500 text-xs mb-10">
          Some endpoints (e.g. <code className="font-mono">POST /api/request-token-safety-analysis</code>) only accept API keys — they trigger multi-second research jobs that don't fit the x402 in-band settlement model. Unpriced endpoints always require an API key.
        </p>

        {/* Examples */}
        <h2 className="text-xl font-semibold mb-3">Examples</h2>

        <div className="mb-6">
          <div className="text-gray-400 text-sm mb-2">API key:</div>
          <CodeBlock>
{`curl -H "x-api-key: YOUR_KEY" \\
  'https://api.deepnets.ai/api/token-safety?mint=<MINT>'`}
          </CodeBlock>
        </div>

        <div className="mb-6">
          <div className="text-gray-400 text-sm mb-2">
            EVM tokens and wallets — pass the 0x address plus <code className="text-gray-300">chain</code>{' '}
            (<code className="text-gray-300">robinhood</code>, <code className="text-gray-300">base</code>,{' '}
            <code className="text-gray-300">ethereum</code>, <code className="text-gray-300">bsc</code>,{' '}
            <code className="text-gray-300">arc</code>) to token-safety, token-safety-history, holder-analysis,
            holder-graph and wallet-details. Omit <code className="text-gray-300">chain</code> for Solana.
          </div>
          <CodeBlock>
{`curl -H "x-api-key: YOUR_KEY" \
  'https://api.deepnets.ai/api/token-safety?mint=0x532f27101965dd16442e59d40670faf5ebb142e4&chain=base'`}
          </CodeBlock>
        </div>

        <div className="mb-10">
          <div className="text-gray-400 text-sm mb-2">
            x402 — first request returns <code className="text-gray-300">402 Payment Required</code> with a{' '}
            <code className="text-gray-300">PAYMENT-REQUIRED</code> header. Any x402-capable client signs a
            USDC transfer and retries with <code className="text-gray-300">X-PAYMENT</code>:
          </div>
          <CodeBlock>
{`# Inspect the 402:
curl -i 'https://api.deepnets.ai/api/token-safety?mint=<MINT>'

# Or from Node — uses @x402/core/client + @solana/kit to handle
# the 402 → sign → retry dance automatically.`}
          </CodeBlock>
        </div>

        <div className="text-center text-gray-500 text-sm pt-4 border-t border-gray-800">
          Questions?{' '}
          <a
            href="mailto:support@deepnets.ai"
            className="text-crypto-accent hover:text-white"
          >
            support@deepnets.ai
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApiDocsPage;
